import { useState, useEffect, useRef } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { styled } from "@mui/material/styles";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";

import {
  Box,
  TextField,
  Button,
  Typography,
  IconButton,
  Snackbar,
  Alert as MuiAlert,
  Card,
  CardContent,
  Stack,
  CircularProgress,
} from "@mui/material";
import { Close as CloseIcon } from '@mui/icons-material';
import {
  //Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  //CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
} from "@mui/icons-material";
import { assignmentService } from "../../services/api";
import { useDropzone } from "react-dropzone";
import { Upload as UploadIcon } from "@mui/icons-material";
const FormSection = styled(Box)(({ theme }) => ({
  marginBottom: theme.spacing(4),
}));

const modules = {
  toolbar: {
    container: [
      [{ header: "1" }, { header: "2" }, { font: [] }],
      [{ size: [] }],
      ["bold", "italic", "underline", "strike", "blockquote"],
      [
        { list: "ordered" },
        { list: "bullet" },
        { indent: "-1" },
        { indent: "+1" },
      ],
      ["clean"],
    ],
  },
  clipboard: { matchVisual: false },
};

const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "blockquote",
  "list",
  "bullet",
  "indent",
  "link",
  "image",
  "video",
];

const DropzoneArea = styled(Box)(({ theme }) => ({
  border: `2px dashed ${theme.palette.grey[300]}`,
  borderRadius: "12px",
  padding: theme.spacing(4),
  backgroundColor: theme.palette.grey[50],
  textAlign: "center",
  cursor: "pointer",
  transition: "all 0.3s ease",
  "&:hover": {
    borderColor: theme.palette.primary.main,
    backgroundColor: theme.palette.grey[100],
  },
}));

/**
 * AssignmentForm is a React component for creating and editing assignments.
 * It displays a form with fields for entering assignment name, description.
 * It also allows for uploading attachments.
 * @returns {React.ReactElement} The rendered form.
 */
const AssignmentForm = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const isEditMode = !!id;

  const [formData, setFormData] = useState({
    assignment_id: "",
    assignment_name: "",
    description: "",
    attachments: [],
    pass_percentage: 0,
  });

  const [loading, setLoading] = useState(false);
  const [fetchLoading, setFetchLoading] = useState(false);
  const [errors, setErrors] = useState({});
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const isSubmitting = useRef(false);
  const [files, setFiles] = useState([]);
const [passPercentage, setPassPercentage] = useState('');
  useEffect(() => {
    if (isEditMode) {
      fetchAssignment();
    }
  }, [id]);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: (acceptedFiles) => {
      setFiles((prev) => [...prev, ...acceptedFiles]);
    },
    accept: {
      "application/pdf": [".pdf"],
      "application/msword": [".doc"],
      "application/vnd.openxmlformats-officedocument.wordprocessingml.document":
        [".docx"],
      "application/vnd.ms-powerpoint": [".ppt"],
      "application/vnd.openxmlformats-officedocument.presentationml.presentation":
        [".pptx"],
      "application/vnd.ms-excel": [".xls"],
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet": [
        ".xlsx",
      ],
      "application/zip": [".zip"],
      "image/*": [".jpg", ".jpeg", ".png", ".gif"],
      "text/plain": [".txt"],
    },
  });

  const fetchAssignment = async () => {
    setFetchLoading(true);
    try {
      const response = await assignmentService.getAssignment(id);

      if (response.data) {
        const assignment = response.data.data;
        setFormData({
          assignment_id: assignment.id,
          assignment_name: assignment.name || "",
          description: assignment.description || "",
          attachments: Array.isArray(assignment.attachments)
            ? assignment.attachments
            : [],
        });
         setPassPercentage(assignment.pass_percentage ? String(assignment.pass_percentage) : '');
      }
    } catch (error) {
      console.error("Error fetching assignment:", error);
      setSnackbar({
        open: true,
        message: "Failed to load assignment data",
        severity: "error",
      });
    } finally {
      setFetchLoading(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));

    // Clear error when user types
    if (errors[name]) {
      setErrors((prev) => ({
        ...prev,
        [name]: null,
      }));
    }
  };

  const handleDescriptionChange = (content) => {
    setFormData((prev) => ({
      ...prev,
      description: content,
    }));
  };

  const removeFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index));
  };

  const removeAttachment = (index) => {
    setFormData((prev) => ({
      ...prev,
      attachments: prev.attachments.filter((_, i) => i !== index),
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    if (!formData.assignment_name.trim()) {
      newErrors.assignment_name = "Assignment name is required";
    }

    if (!passPercentage) {
            newErrors.passPercentage = "Pass percentage is required";
        } else if (passPercentage < 35 || passPercentage > 100) {
            newErrors.passPercentage = "Enter a valid pass percentage between 35 and 100";
        }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (isDraft = false) => {
    if (isSubmitting.current) return;
    isSubmitting.current = true;
    if (!validateForm()) {
      setSnackbar({
        open: true,
        message: "Please fill all required fields",
        severity: "error",
      });
      isSubmitting.current = false;
      return;
    }



// ✅ Duplicate check
  try {
    const existingNameCheck = await assignmentService.checkAssignmentName(
      formData.assignment_name,
      isEditMode ? id : null
    );

    if (existingNameCheck.data.exists) {
      setErrors((prev) => ({
        ...prev,
        assignment_name: "This assignment name already exists",
      }));
      setSnackbar({
        open: true,
        message: "Duplicate assignment name",
        severity: "error",
      });
      isSubmitting.current = false;
      return;
    }
  } catch (error) {
    console.error("Error checking duplicate:", error);
    setSnackbar({
      open: true,
      message: "Error checking assignment name",
      severity: "error",
    });
    isSubmitting.current = false;
    return;
  }



    setLoading(true);
    try {
      const formPayload = new FormData();
      formPayload.append("assignment_name", formData.assignment_name);
      formPayload.append("description", formData.description || "");
      formPayload.append("save", isDraft ? "draft" : "publish");
      formPayload.append("pass_percentage", passPercentage);

      if (isEditMode) {
        formPayload.append("assignment_id", id);
      }

      // Add files to form data
      files.forEach((file) => {
        formPayload.append("attachments", file);
      });

      // Handle attachment deletions if any
      if (isEditMode && formData.attachments) {
        const originalAttachmentIds = formData.attachments
          .map((att) => att.id)
          .filter(Boolean);
        formPayload.append(
          "keep_attachments",
          JSON.stringify(originalAttachmentIds)
        );
      }

      const response = await assignmentService.createAssignment(formPayload);

      setSnackbar({
        open: true,
        message: `Assignment ${
          isEditMode ? "updated" : "created"
        } successfully!`,
        severity: "success",
      });

      setTimeout(() => {
        navigate("/assignments");
      }, 1000);
    } catch (error) {
      console.error("Error saving assignment:", error);
      setSnackbar({
        open: true,
        message: error.response?.data?.message || "Error saving assignment",
        severity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  if (fetchLoading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 1200, mx: "auto", p: 3 }}>
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <MuiAlert
          severity={snackbar.severity}
          variant="filled"
          onClose={() => setSnackbar((prev) => ({ ...prev, open: false }))}
        >
          {snackbar.message}
        </MuiAlert>
      </Snackbar>

      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4">
          {id ? "Edit Assignment" : "Create New Assignment"}
        </Typography>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate("/assignments")}
        >
          Back to Assignments
        </Button>
      </Box>

      <form
        onSubmit={(e) => {
          e.preventDefault();
          handleSubmit(false);
        }}
      >
        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Basic Information
          </Typography>
          <TextField
            fullWidth
            label="Assignment Name"
            name="assignment_name"
            value={formData.assignment_name}
            onChange={handleChange}
            variant="outlined"
            sx={{ mb: 3 }}
            error={!!errors.assignment_name}
            helperText={errors.assignment_name}
            required
          />
        </FormSection>

        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Assignment Description
          </Typography>
          <ReactQuill
            theme="snow"
            value={formData.description}
            onChange={handleDescriptionChange}
            style={{ height: "200px", marginBottom: "50px" }}
            modules={modules}
            formats={formats}
          />
        </FormSection>

        <FormSection>
          <Typography variant="h6" sx={{ mb: 3 }}>
            Attachments
          </Typography>

          {/* Existing attachments */}
          {formData.attachments.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Current Attachments
              </Typography>
              <Stack spacing={1}>
                {formData.attachments.map((attachment, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <CardContent sx={{ flexGrow: 1, py: 1 }}>
                      <Typography variant="body2">
                        {attachment.name || attachment.filename || "Attachment"}
                      </Typography>
                    </CardContent>
                    <IconButton
                      color="error"
                      onClick={() => removeAttachment(index)}
                      sx={{ mr: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          {/* New attachments */}
          {files.length > 0 && (
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                New Attachments
              </Typography>
              <Stack spacing={1}>
                {files.map((file, index) => (
                  <Card
                    key={index}
                    variant="outlined"
                    sx={{ display: "flex", alignItems: "center" }}
                  >
                    <CardContent sx={{ flexGrow: 1, py: 1 }}>
                      <Typography variant="body2">{file.name}</Typography>
                    </CardContent>
                    <IconButton
                      color="error"
                      onClick={() => removeFile(index)}
                      sx={{ mr: 1 }}
                    >
                      <DeleteIcon />
                    </IconButton>
                  </Card>
                ))}
              </Stack>
            </Box>
          )}

          <DropzoneArea {...getRootProps()}>
            <input {...getInputProps()} />
            <UploadIcon sx={{ fontSize: 48, color: "primary.main", mb: 2 }} />
            <Typography variant="body1" sx={{ mb: 1 }}>
              {isDragActive ? "Drop files here" : "Drag and drop files here"}
            </Typography>
            <Typography variant="body2" color="textSecondary">
              or click to browse
            </Typography>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 2, display: "block" }}
            >
              Supported formats: PDF, Word, PowerPoint, Excel, ZIP, Images, Text
            </Typography>
          </DropzoneArea>
        </FormSection>

        <Box mt={2}>
          <TextField
            label="Pass Percentage"
            type="number"
            inputProps={{ min: 1, max: 100 }}
             value={passPercentage}
            onChange={(e) => setPassPercentage(e.target.value)}
            required
            fullWidth
            margin="normal"
            error={!!errors.passPercentage}
            helperText={
              errors.passPercentage ||
              "Set the minimum percentage required to pass."
            }
          />
        </Box>
        <Box
          sx={{ mt: 3, display: "flex", justifyContent: "flex-end", gap: 2 }}
        >
          <Button
            type="submit"
            variant="contained"
            color="primary"
            disabled={loading}
          >
            {loading
              ? "Submitting..."
              : isEditMode
              ? "Update Assignment"
              : "Publish"}
          </Button>

          <Button
            variant="outlined"
            onClick={() => handleSubmit(true)}
            disabled={loading}
            sx={{ ml: 2 , px: 3, py: 1  }}
            
          >
            {loading ? "Submitting..." : "Save as Draft"}
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AssignmentForm;
